import { z } from 'zod'

import type {
  DaypartingAdType,
  DaypartingEventStatus,
  DaypartingMetric,
  DaypartingScope,
} from '@/client'

/**
 * The dayparting page's view state, read from the URL.
 *
 * Beside the page rather than in the route file, so imports point one way and
 * the pruning below is covered. Every field is optional with a `.catch`,
 * because a query string is user input; defaults are applied at read time and
 * stripped on the way out, so `/marketing/dayparting` stays the clean URL of
 * the default view.
 *
 * The selection (`ids`) is here too: it decides what the weekday profile shows,
 * which makes it view state, and a link to "these five campaigns, Tuesdays"
 * is the point of putting it in the URL. The schedule being drafted is not —
 * that is a form, and form drafts stay out of history.
 */

export const SCOPES = ['CAMPAIGN', 'AD_GROUP', 'TARGET'] as const satisfies DaypartingScope[]
export const AD_TYPES = ['SP', 'SB', 'SD'] as const satisfies DaypartingAdType[]
export const METRICS = [
  'clicks',
  'impressions',
  'spend',
  'sales',
  'units',
  'ctr',
  'cpc',
  'acos',
  'roas',
] as const satisfies DaypartingMetric[]

/** The event log's outcomes, in the order the filter offers them (VentaHub's). */
export const EVENT_STATUSES = [
  'success',
  'failed',
  'skipped',
] as const satisfies DaypartingEventStatus[]

/** The backend reads at most this many days; the page explains rather than asks. */
export const MAX_PROFILE_DAYS = 180

/** The backend accepts at most this many entities in one schedule. */
export const MAX_SELECTED = 500

const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined)

export const daypartingSearchSchema = z.object({
  store: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe(
      'The store whose campaigns, ad groups and targets are scheduled, by id. Nothing loads without it.',
    ),
  scope: z
    .enum(SCOPES)
    .optional()
    .catch(undefined)
    .describe('Level picked from: campaigns, ad groups or targets; default CAMPAIGN.'),
  adType: z
    .enum(AD_TYPES)
    .optional()
    .catch(undefined)
    .describe('Ad type of the entities listed (SP, SB or SD); default SP.'),
  /** Free text over the entity list. */
  q: z.string().max(200).optional().catch(undefined).describe('Search over the entity list.'),
  /** The selected entities' Amazon ids. */
  ids: z
    .array(z.string().min(1).max(64))
    .max(MAX_SELECTED)
    .optional()
    .catch(undefined)
    .describe(
      'Amazon ids of the selected entities, which the weekday profile and a new schedule cover.',
    ),
  metric: z
    .enum(METRICS)
    .optional()
    .catch(undefined)
    .describe('Metric the weekday profile shows; default clicks.'),
  start: day.describe(
    'First day of the weekday profile range, YYYY-MM-DD; default 30 days ago, at most 180 days.',
  ),
  end: day.describe('Last day of the weekday profile range, YYYY-MM-DD; default today.'),
  /** The page of the schedule list. */
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the schedule list, 1-based; default 1.'),
  /** The event log's outcome filter; absent shows every outcome. */
  events: z
    .enum(EVENT_STATUSES)
    .optional()
    .catch(undefined)
    .describe('Event log outcome filter; absent shows every outcome.'),
  /** The page of the event log. */
  eventsPage: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the event log, 1-based; default 1.'),
})

export type DaypartingSearch = z.infer<typeof daypartingSearchSchema>

/** Drop every value that equals what the page would assume anyway. */
export function withoutDaypartingSearchDefaults(search: DaypartingSearch): DaypartingSearch {
  const pruned: DaypartingSearch = {}
  if (search.store !== undefined) pruned.store = search.store
  if (search.scope !== undefined && search.scope !== 'CAMPAIGN') pruned.scope = search.scope
  if (search.adType !== undefined && search.adType !== 'SP') pruned.adType = search.adType
  if (search.q) pruned.q = search.q
  if (search.ids && search.ids.length > 0) pruned.ids = search.ids
  if (search.metric !== undefined && search.metric !== 'clicks') pruned.metric = search.metric
  if (search.start) pruned.start = search.start
  if (search.end) pruned.end = search.end
  if (search.page !== undefined && search.page !== 1) pruned.page = search.page
  if (search.events !== undefined) pruned.events = search.events
  if (search.eventsPage !== undefined && search.eventsPage !== 1) {
    pruned.eventsPage = search.eventsPage
  }
  return pruned
}
